import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useMessagingStore } from "@/stores/messaging-store";
import {
  createMessagingSocket,
  type MessagingSocket,
} from "@/lib/messaging-socket";
import {
  addOrReplaceMessageInCache,
  bumpConversationInCache,
  dropConversationFromCache,
  incrementConversationUnread,
  messagingKeys,
  patchMessageInCache,
  patchReplyPreviewsInCache,
  refreshConversationPreviewIfLatest,
  removeMessageFromCache,
  useMessagingUnreadCount,
} from "@/lib/tanstack-query/messaging";
import type {
  MessagesPage,
  MessageResponseDto,
} from "@/lib/tanstack-query/dtos/messaging.dto";
import type { InfiniteData } from "@tanstack/react-query";
import { MessagingContext } from "./messaging.context";

const TYPING_SWEEP_INTERVAL_MS = 1000;

export function MessagingProvider({ children }: { children: React.ReactNode }) {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const [socket, setSocket] = useState<MessagingSocket | null>(null);
  const socketRef = useRef<MessagingSocket | null>(null);

  const setUnreadCount = useMessagingStore((s) => s.setUnreadCount);
  const bumpUnread = useMessagingStore((s) => s.bumpUnread);
  const setSocketConnected = useMessagingStore((s) => s.setSocketConnected);
  const setTyping = useMessagingStore((s) => s.setTyping);
  const clearExpiredTyping = useMessagingStore((s) => s.clearExpiredTyping);
  const setPresence = useMessagingStore((s) => s.setPresence);
  const reconcilePendingWithServer = useMessagingStore(
    (s) => s.reconcilePendingWithServer,
  );
  const resetStore = useMessagingStore((s) => s.reset);

  const { data: unreadData } = useMessagingUnreadCount(Boolean(session));

  useEffect(() => {
    if (!unreadData) return;
    setUnreadCount(unreadData.count);
  }, [unreadData, setUnreadCount]);

  useEffect(() => {
    const token = session?.access_token;
    if (!token) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setSocket(null);
      resetStore();
      return;
    }
    if (socketRef.current) return;

    const next = createMessagingSocket(token);
    socketRef.current = next;
    setSocket(next);

    next.on("connect", () => setSocketConnected(true));
    next.on("disconnect", () => setSocketConnected(false));
    next.on("connect_error", () => setSocketConnected(false));

    next.on("messages:unread", ({ count }) => setUnreadCount(count));

    next.on("messages:unread:delta", ({ delta }) => {
      bumpUnread(delta);
      const nextCount = useMessagingStore.getState().unreadCount;
      if (nextCount < 0 || delta < 0) {
        queryClient.invalidateQueries({
          queryKey: messagingKeys.unreadCount(),
        });
      }
    });

    next.on("message:new", (message) => {
      addOrReplaceMessageInCache(queryClient, message);
      bumpConversationInCache(queryClient, message);
      reconcilePendingWithServer(message);
      if (message.type === "SYSTEM") {
        queryClient.invalidateQueries({
          queryKey: messagingKeys.participants(message.conversationId),
        });
      }
      const { activeConversationId } = useMessagingStore.getState();
      if (
        message.senderId !== session?.user.id &&
        activeConversationId !== message.conversationId
      ) {
        incrementConversationUnread(queryClient, message.conversationId);
      }
    });

    next.on("message:edited", (message) => {
      patchMessageInCache(queryClient, message);
      patchReplyPreviewsInCache(
        queryClient,
        message.conversationId,
        message.id,
        {
          body: message.body,
          deletedAt: message.deletedAt,
        },
      );
      refreshConversationPreviewIfLatest(queryClient, message);
    });

    next.on("message:deleted", ({ id, conversationId, deletedAt }) => {
      const cache = queryClient.getQueryData<InfiniteData<MessagesPage>>(
        messagingKeys.messageList(conversationId),
      );
      const existing = cache?.pages
        .flatMap((p) => p.results)
        .find((m) => m.id === id);
      if (!cache && !existing) {
        removeMessageFromCache(queryClient, conversationId, id);
        return;
      }
      const patched: MessageResponseDto = {
        id,
        conversationId,
        senderId: existing?.senderId ?? null,
        senderIdentity: existing?.senderIdentity ?? "SELF",
        type: existing?.type ?? "TEXT",
        body: null,
        attachment: null,
        createdAt: existing?.createdAt ?? deletedAt,
        editedAt: existing?.editedAt ?? null,
        deletedAt,
        senderBlockedAt: existing?.senderBlockedAt ?? null,
        metadata: existing?.metadata ?? null,
        replyTo: existing?.replyTo ?? null,
      };
      patchMessageInCache(queryClient, patched);
      patchReplyPreviewsInCache(queryClient, conversationId, id, {
        body: null,
        deletedAt,
      });
      refreshConversationPreviewIfLatest(queryClient, patched);
    });

    next.on("participant:added", ({ conversationId }) => {
      queryClient.invalidateQueries({
        queryKey: messagingKeys.conversations(),
      });
      queryClient.invalidateQueries({
        queryKey: messagingKeys.participants(conversationId),
      });
    });

    next.on("participant:removed", ({ conversationId }) => {
      dropConversationFromCache(queryClient, conversationId);
      queryClient.invalidateQueries({
        queryKey: messagingKeys.participants(conversationId),
      });
    });

    next.on("typing", (payload) => {
      if (payload.identity === "SELF") {
        if (payload.userId === session?.user.id) return;
        setTyping(payload.conversationId, {
          identity: "SELF",
          userId: payload.userId,
        });
      } else {
        setTyping(payload.conversationId, { identity: "ANON" });
      }
    });

    next.on("presence:update", (payload) => {
      if (!payload.conversationId) return;
      if (payload.identity === "ANON") {
        setPresence(payload.conversationId, {
          identity: "ANON",
          online: payload.online,
        });
      } else {
        setPresence(payload.conversationId, {
          identity: payload.identity,
          userId: payload.userId,
          online: payload.online,
        });
      }
    });

    next.connect();

    return () => {
      next.disconnect();
      socketRef.current = null;
      setSocket(null);
    };
  }, [
    session?.access_token,
    session?.user.id,
    queryClient,
    setSocketConnected,
    setUnreadCount,
    bumpUnread,
    setTyping,
    setPresence,
    reconcilePendingWithServer,
    resetStore,
  ]);

  useEffect(() => {
    const i = setInterval(clearExpiredTyping, TYPING_SWEEP_INTERVAL_MS);
    return () => clearInterval(i);
  }, [clearExpiredTyping]);

  const value = useMemo(
    () => ({
      socket,
      subscribe: (conversationId: string) =>
        new Promise<void>((resolve, reject) => {
          if (!socket) return reject(new Error("Socket not connected"));
          socket.emit("subscribe", { conversationId }, (ack) => {
            if (ack.ok) resolve();
            else reject(new Error(ack.error));
          });
        }),
      emitTyping: (conversationId: string, isTyping: boolean) => {
        socket?.emit("typing", { conversationId, isTyping });
      },
    }),
    [socket],
  );

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
}
